import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table'
import { IconContext } from 'react-icons'
import { FaAngleUp, FaAngleDown } from 'react-icons/fa'
import { TableContext } from './table/TableContext'
import { SimpleSpinner } from '../../elem/Spinner'
import NewUploadButton from './NewUploadButton'
import { DataContext } from './DataContext'
import BulkSubmitButton from './table/BulkSubmitButton'
import { useLocation } from 'react-router-dom'
import Pagination from '../../elem/table/shared/Pagination'
import {
    getInProgressColumns,
    getSubmittedColumns,
} from '../../../utils/submissions/table'
import { UserContext } from '../../wrappers/UserContext'
import { isReviewerRole } from '../../../utils/user/permissions'
import ConfirmDelete from './table/ConfirmDelete'
import {
    DefaultColumnFilter,
    fuzzyTextFilterFn,
    getHeaderProps,
    IndeterminateCheckbox,
    selectionColumn,
} from '../../elem/table/SimpleTable'
import NoDataComponent from './table/NoDataComponent'

const TableDisplay = ({
    headerGroups,
    rows,
    sortable,
    filterable,
    columnsCount,
}) => {
    return (
        <div className="gridWrapper">
            <table className="table is-fullwidth is-striped">
                <thead
                    className="sticky-top"
                    style={{ backgroundColor: 'white' }}
                >
                    {headerGroups.map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th
                                    key={header.id}
                                    className="is-size-7 vertical-align"
                                >
                                    <div className="explorerTableHeader simpleTableHeader">
                                        <div
                                            className="nameAndSort"
                                            {...getHeaderProps(header, sortable)}
                                        >
                                            {' '}
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {sortable && header.column.getIsSorted() && (
                                                <span className="explorerTableArrow simpleTableArrow">
                                                    <IconContext.Provider
                                                        value={{
                                                            className: 'has-text-link',
                                                            size: '1.1rem',
                                                        }}
                                                    >
                                                        {header.column.getIsSorted() === 'desc' ? (
                                                            <FaAngleDown />
                                                        ) : (
                                                            <FaAngleUp />
                                                        )}
                                                    </IconContext.Provider>
                                                </span>
                                            )}
                                        </div>
                                        {filterable && header.column.getCanFilter() && (
                                            <div className="filter">
                                                <DefaultColumnFilter column={header.column} />
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {rows.length > 0 ? (
                        rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan={columnsCount}
                                className="has-text-centered p-4"
                            >
                                No data available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    )
}

const tabNameToStatus = {
    submitted: 'Submitted',
    approved: 'Approved',
    denied: 'Denied',
}

const selectableTabs = ['submitted', 'inProgress']

const SubmissionTabs = ({ onChange, active }) => (
    <div className="tabs is-large">
        <ul>
            <li
                className={`${active === 'inProgress' ? 'is-active' : ''}`}
                onClick={() => onChange('inProgress')}
            >
                <span className="link">Forms In Progress</span>
            </li>
            <li
                className={`${active === 'submitted' ? 'is-active' : ''}`}
                onClick={() => onChange('submitted')}
            >
                <span className="link">Submitted Forms</span>
            </li>
            <li
                className={`${active === 'approved' ? 'is-active' : ''}`}
                onClick={() => onChange('approved')}
            >
                <span className="link">Approved Forms</span>
            </li>
            <li
                className={`${active === 'denied' ? 'is-active' : ''}`}
                onClick={() => onChange('denied')}
            >
                <span className="link">Denied Forms</span>
            </li>
        </ul>
        <NewUploadButton />
    </div>
)

export default () => {
    const { state: locationState } = useLocation()
    const topRef = useRef(null)
    const sectionRef = useRef(null)
    const [topHeight, setTopHeight] = useState(0)

    const {
        uploads,
        setSelectedUploads,
        setConfirmDeleteModalData,
        deleteFunction,
    } = useContext(TableContext)
    const { roles } = useContext(UserContext)
    const { initializeState, setActiveAgency, setErrorState } =
        useContext(DataContext)

    const [activeTab, setActiveTab] = useState('inProgress')
    const isReviewer = useMemo(() => isReviewerRole(roles), [roles])
    const [rowSelection, setRowSelection] = useState({})
    const [sorting, setSorting] = useState([])
    const [columnFilters, setColumnFilters] = useState([])
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 })

    const currentData = useMemo(() => {
        if (!uploads) return []

        const data =
            activeTab === 'inProgress'
                ? uploads.incomplete || []
                : (uploads.complete || []).filter(
                      (upload) => upload.Status === tabNameToStatus[activeTab]
                  )

        return [...data].sort(
            (a, b) => Number(b['Form ID']) - Number(a['Form ID'])
        )
    }, [uploads, activeTab])

    const currentColumns = useMemo(() => {
        if (activeTab === 'inProgress') {
            return getInProgressColumns(currentData, setConfirmDeleteModalData)
        } else {
            return getSubmittedColumns(currentData, isReviewer, activeTab)
        }
    }, [activeTab, currentData, isReviewer, setConfirmDeleteModalData])

    const allColumns = useMemo(() => {
        if (selectableTabs.includes(activeTab)) {
            return [selectionColumn, ...currentColumns]
        }
        return currentColumns
    }, [currentColumns, activeTab])

    const table = useReactTable({
        columns: allColumns,
        data: currentData,
        state: {
            rowSelection,
            sorting,
            columnFilters,
            pagination,
        },
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        enableRowSelection: selectableTabs.includes(activeTab),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        filterFns: { fuzzyText: fuzzyTextFilterFn },
        defaultColumn: {
            filterFn: 'includesString',
        },
        autoResetPageIndex: false,
        sortDescFirst: false,
    })

    useEffect(() => {
        if (
            selectableTabs.includes(activeTab) &&
            setSelectedUploads &&
            rowSelection
        ) {
            const selectedIdx = Object.keys(rowSelection)
            const associatedRows = currentData.filter((x, idx) =>
                selectedIdx.includes(idx.toString())
            )
            setSelectedUploads(associatedRows)
        }
    }, [rowSelection, setSelectedUploads, currentData, activeTab])

    useEffect(() => {
        if (selectableTabs.includes(activeTab)) {
            setRowSelection({})
        }
    }, [columnFilters, activeTab])

    useEffect(() => {
        setRowSelection({})
    }, [uploads])

    useEffect(() => {
        setSorting([])
        setColumnFilters([])
        setPagination((prev) => ({ ...prev, pageIndex: 0 }))
    }, [activeTab])

    useEffect(() => {
        setActiveAgency(null)
        initializeState({})
        setErrorState({})
        setActiveTab(
            locationState && locationState.from
                ? locationState.from
                : 'inProgress'
        )
    }, [])

    useEffect(() => {
        const updateHeight = () => {
            if (topRef.current) {
                const height = topRef.current.getBoundingClientRect().height
                setTopHeight(height)
            }
        }

        updateHeight()
        const resizeObserver = new ResizeObserver(updateHeight)

        if (topRef.current) {
            resizeObserver.observe(topRef.current)
        }

        window.addEventListener('resize', updateHeight)

        return () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', updateHeight)
        }
    }, [])

    useEffect(() => {
        if (sectionRef.current) {
            sectionRef.current.style.maxHeight = `calc(100vh - ${topHeight}px - 2.3rem)`
        }
    }, [topHeight])

    const loading = !uploads

    return (
        <div className="container uploads">
            <ConfirmDelete deleteFunction={deleteFunction} />

            <div ref={topRef}>
                <SubmissionTabs onChange={setActiveTab} active={activeTab} />
                <div className="bulkSubmitWrapper">
                    <div className="columns">
                        {activeTab === 'inProgress' ? (
                            <BulkSubmitButton action="Submit" />
                        ) : activeTab === 'submitted' ? (
                            <>
                                <BulkSubmitButton action="Approve" />
                                <BulkSubmitButton action="Deny" />
                            </>
                        ) : null}
                    </div>
                    {!loading && (
                        <Pagination
                            extraClass="submissionsPagination"
                            backgroundColorClass="background-2"
                            selectDropDownCaratClass="carat-class-2"
                            gotoPage={table.setPageIndex}
                            previousPage={table.previousPage}
                            canPreviousPage={table.getCanPreviousPage()}
                            nextPage={table.nextPage}
                            canNextPage={table.getCanNextPage()}
                            pageCount={table.getPageCount()}
                            resultsCount={currentData.length}
                            pageOptions={Array.from({ length: table.getPageCount() }, (_, i) => i)}
                            pageIndex={pagination.pageIndex}
                            pageSize={pagination.pageSize}
                            setPageSize={table.setPageSize}
                        />
                    )}
                </div>
            </div>

            <div ref={sectionRef}>
                {loading ? (
                    <SimpleSpinner />
                ) : (
                    <div className="submissionsTableWrapper">
                        {!currentData || !currentData.length ? (
                            <NoDataComponent
                                message={`There are no ${
                                    activeTab === 'inProgress'
                                        ? 'Forms In Progress'
                                        : `${tabNameToStatus[activeTab]} Forms`
                                } to display.`}
                            />
                        ) : null}
                        <div
                            className={`${
                                !currentData || !currentData.length
                                    ? 'is-hidden'
                                    : ''
                            }`}
                        >
                            <TableDisplay
                                headerGroups={table.getHeaderGroups()}
                                rows={table.getRowModel().rows}
                                sortable={true}
                                filterable={true}
                                columnsCount={allColumns.length}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
